import { Editor } from '@tiptap/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Box, Button, HStack, IconButton, MicrophoneOutline, SparklesOutline, Spinner, StopSolid, Tooltip } from '@bonusly/design-system';

import { AudioState, SourceMode, Transcribe } from './hooks/use-audio-transcription';

type TranscribeControlsProps = {
	editor: Editor | null;
	transcribe: Transcribe;
};

export const TranscribeControls: FC<TranscribeControlsProps> = ({ editor, transcribe }) => {
	const { t } = useTranslation('feedback');

	const { mode, state } = transcribe;

	const isStarting = state === AudioState.Starting,
		isRecording = state === AudioState.Recording,
		isStopping = state === AudioState.Stopping,
		isComplete = state === AudioState.Complete,
		isDoneRecording = state >= AudioState.Stopping,
		isRephrased = mode === SourceMode.Rephrased,
		isRaw = mode === SourceMode.Raw,
		isRephrasing = state === AudioState.Stopping || state === AudioState.Rephrasing;

	let showRecord = state < AudioState.Recording;

	// If we're not already recording then we disable the record button if the user has started typing
	if (transcribe.state === AudioState.Idle && !!editor?.getText().trim()) {
		showRecord = false;
	}

	return (
		<HStack justifyContent="flex-end" columnGap={1}>
			{showRecord && (
				<>
					{isStarting && (
						<Badge borderRadius="sm" colorScheme="blue" px={2} variant="subtle">
							{t('transcribe.connecting')}
						</Badge>
					)}
					<Tooltip label={t('transcribe.start_label')} placement="top">
						<Box>
							<IconButton
								aria-label={t('transcribe.start_label')}
								color="primary.fgStrong"
								bgColor="primary.bgAlpha"
								isLoading={isStarting}
								onClick={transcribe.start}
							>
								<MicrophoneOutline />
							</IconButton>
						</Box>
					</Tooltip>
				</>
			)}

			{isRecording && (
				<>
					<Badge borderRadius="sm" colorScheme="blue" px={2} variant="subtle">
						{t('transcribe.recording')}
					</Badge>

					<Tooltip label={t('transcribe.stop_label')} placement="top">
						<Box>
							<IconButton
								aria-label={t('transcribe.stop_label')}
								color="primary.fgStrong"
								bgColor="primary.bgAlpha"
								isLoading={isStopping}
								onClick={transcribe.stop}
							>
								<StopSolid />
							</IconButton>
						</Box>
					</Tooltip>
				</>
			)}

			{isComplete && isRephrased && (
				<Button onClick={transcribe.sourceRaw} size="sm" variant="ghost">
					{t('transcribe.use_raw')}
				</Button>
			)}

			{isDoneRecording && isRaw && (
				<Button isDisabled={isRephrasing} onClick={transcribe.sourceRephrased} size="sm" variant="ghost">
					{isRephrasing ? <Spinner size="sm" mr={2} /> : <SparklesOutline size="sm" mr={1} />}

					{t('transcribe.use_rephrased')}
				</Button>
			)}
		</HStack>
	);
};
